/**
 * 相対取引契約 CSV の frontend パーサ / シリアライザ。
 *
 * 列構成の SoT は backend の `BilateralTradeCsvHeader`
 * (`go/internal/backend/application/usecase/supply/bilateral_trade_csv_parse.go`)
 * で、frontend はこれと **完全一致** させる (= 厳格モード: 列名・順序・列数が違う
 * CSV は受け付けない)。英語ヘッダー (`BILATERAL_TRADE_CSV_COLUMNS_EN` = backend の
 * `BilateralTradeCsvHeaderEnglish`) も受理し、こちらは大文字・小文字を無視する。列の必須区分・形式・値域は
 * `docs/specs/admin/data/uploads/bilateral-trade.md` 第2節「相対取引契約 CSV
 * 列フォーマット」が SoT。
 *
 * 業務バリデーション・差分判定 (5 状態) は backend の 2 段 API
 * (CheckBilateralTradeCsv / ApplyBilateralTradeCsv) が唯一の SoT。ここは
 *
 *   - テンプレート DL 用のヘッダー
 *   - アップロード前の構造チェック (ヘッダー不一致 / 列数ズレ) と
 *     プレビュー表示用のセル取り出し
 *   - 入稿済みデータ DL のための行 → CSV テキスト復元 (往復互換)
 *   - 時間帯区分のコマ配列 ↔ "HH:mm-HH:mm" 相互変換 (往復可逆)
 *
 * だけを担う。
 *
 * 燃調 (適用フラグ / プロファイル) の列は存在しない。オプション設定
 * （オプション有無・基本料・通告ルール・備考）・利用率下限/上限・受渡カレンダー
 * 例外も CSV では表現しない（個別入力画面のみ。spec スコープ外セクション参照）。
 */
import { escapeCsvCell } from '~/libs/csv'
import type { ColumnGuideEntry } from '~/models/columnGuide'
import {
  type AcceptedCsvHeaders,
  isAcceptedCsvHeader,
} from '~/models/csvAcceptedHeaders'
import { isBlankCsvRecord, readCsvRecords } from '~/models/csvRecords'
import { komaEndTime, komaNumberToTime } from '~/models/komaToTime'

/**
 * 相対取引契約 CSV の列 (1 行 = 1 取引ID = 1 独立契約、列順固定、20 列)。
 *
 * leg 概念の廃止 (2026-08-24 PdM 方針転換、issue #6131) により、旧列4
 * グループ番号 (突合キーでも役割でもない任意ラベル) は削除した (21→20 列)。
 * 本 CSV は同一取引ID内で複数行を束ねる仕組みを持たない — 期間分割・
 * スプレッドはそれぞれ別の取引IDで 1 件ずつ入稿する。
 */
export const BILATERAL_TRADE_CSV_COLUMNS = [
  '約定経路',
  '約定日',
  '取引ID',
  '自社側事業者コード',
  '自社側BG/計画提出者コード',
  '取引先',
  '取引先BG/計画提出者コード',
  '商品形態',
  '受渡区分',
  '売買',
  '自社エリア',
  '精算単価参照エリア',
  '開始日',
  '終了日',
  '日区分',
  '時間帯区分',
  '受給日カレンダー',
  '数量 (MW)',
  '単価 (円/kWh)',
  'delete',
] as const

export type BilateralTradeCsvColumn =
  (typeof BILATERAL_TRADE_CSV_COLUMNS)[number]

/**
 * ヘッダー 1 列分が、指定言語の期待列名に一致するか (厳格モード: 列名・順序が一致)。
 * 日本語は完全一致、英語は大文字・小文字を区別しない (`conventions.md`「CSV の英語表示」。
 * backend の取込と同じ判定)。
 */
function isHeaderCellOf(
  cell: string,
  index: number,
  isEnglish: boolean,
): boolean {
  return isEnglish
    ? cell.toLowerCase() === BILATERAL_TRADE_CSV_COLUMNS_EN[index].toLowerCase()
    : cell === BILATERAL_TRADE_CSV_COLUMNS[index]
}

/** テンプレート DL の 1 行目 (日本語表示。= 厳格モードの期待ヘッダー)。 */
export const BILATERAL_TRADE_CSV_HEADER = BILATERAL_TRADE_CSV_COLUMNS.join(',')

/**
 * 記入ガイド表の単一 SoT。**文言の正本は spec `bilateral-trade.md`
 * 第3節「画面表示（テンプレート準備画面）」の表**で、説明 / 入力形式 / 記入例 の
 * 3 列をそのまま持つ (spec §3 ビジネスルール: 「第2節の形式・値域にある内部参照
 * 〈列番号参照・AC番号参照・正規化ロジックの詳細〉は画面には出さない。お客様が
 * 読んでそのまま理解できる入力手順として書ける粒度に留める」)。実装側で独自の
 * 説明文を持たない — 文言変更は spec の更新をもって行う。
 *
 * `example` は spec §3 の「記入例」列であり、テンプレート CSV の記入例行
 * (`BILATERAL_TRADE_CSV_TEMPLATE_ROWS` = spec §2「雛形の記入例データ」) とは
 * 別の正本。前者は「1 列だけを見たときの書き方の例」、後者は「4 パターンの
 * 行として整合する実データ」で、spec が両方を別々に定義している。
 *
 * 列順は `BILATERAL_TRADE_CSV_COLUMNS` と完全一致させる。型は共有
 * `ColumnGuideEntry<BilateralTradeCsvColumn>` を使う (issue #6467 レビュー
 * 指摘: 独自の双子型 `BilateralTradeCsvColumnRequirement` /
 * `BilateralTradeCsvColumnGuideEntry` を持つと、共有型と構造的部分型で偶然
 * 通っているだけの関係になり、どちらか片方に区分を足しても他方が追随しない)。
 */
export const BILATERAL_TRADE_CSV_COLUMN_GUIDE: Array<
  ColumnGuideEntry<BilateralTradeCsvColumn>
> = [
  {
    name: '約定経路',
    description: '取引が成立した市場・経路',
    required: 'required',
    rule: '「enechain / eSquare Live / ベースロード市場 / 先渡し市場 / 常時BU / その他」のいずれかを入力。',
    example: 'enechain',
  },
  {
    name: '約定日',
    description: '取引が約定した日',
    required: 'required',
    rule: '「YYYY/MM/DD」の形式で入力。',
    example: '2026/09/01',
  },
  {
    name: '取引ID',
    description: '自社で任意に採番する識別子',
    required: 'required',
    rule: "重複しない値で20文字以内で入力。先頭に = + - @ ' は使えない。期間分割・スプレッドの場合も、それぞれ別の取引IDで1行ずつ入力する。",
    example: 'TXN-20260901-001',
  },
  {
    name: '自社側事業者コード',
    description: '契約した自社の事業者コード',
    required: 'required',
    rule: '広域機関の事業者タブの事業者コードを入力 (4桁コード)。自社エリアの自社 BG に構成事業者として登録され、受渡開始日の時点で有効な事業者に限る (合同 BG では構成他社の事業者も可)。',
    example: 'R001',
  },
  {
    name: '自社側BG/計画提出者コード',
    description: '自社のBGコードまたは計画提出者コード',
    required: 'required',
    rule: '広域機関のBGタブのBGコード、または計画提出者マスタの計画提出者コードを入力。',
    example: 'LB003',
  },
  {
    name: '取引先',
    description: '取引相手の名称',
    required: 'conditional',
    rule: '広域機関の取引先タブの「取引先名称」または「略称」を入力。受渡区分が JEPX / TOCOM / EEX / ICE の場合は任意 (空欄可)。',
    example: '取引先A',
  },
  {
    name: '取引先BG/計画提出者コード',
    description:
      '取引先のBGコードまたは計画提出者コード (取引先エリアを兼ねる)',
    required: 'conditional',
    rule: '広域機関の取引先タブの取引先コードまたは計画提出者マスタの計画提出者コードを入力。末尾1桁がエリアコードとして取引先エリアの特定に使われる。受渡区分が JEPX / TOCOM / EEX / ICE の場合は任意（空欄可）。',
    example: 'LDEF3',
  },
  {
    name: '商品形態',
    description: '数量の表現方式',
    required: 'required',
    rule: '「定型」または「非定型」で入力。',
    example: '定型',
  },
  {
    name: '受渡区分',
    description: '受渡の経路区分',
    required: 'required',
    rule: '「BG / JEPX / EEX / TOCOM / ICE / スワップ」のいずれかを入力。約定経路が「ベースロード市場」または「先渡し市場」の場合は「JEPX」のみ入力可。約定経路が「常時BU」の場合は「BG」のみ入力可。',
    example: 'JEPX',
  },
  {
    name: '売買',
    description: '自社から見た売買の方向',
    required: 'required',
    rule: '「買」または「売」を入力。',
    example: '買',
  },
  {
    name: '自社エリア',
    description: '取引の自社側エリア',
    required: 'required',
    rule: '「北海道 / 東北 / 東京 / 中部 / 北陸 / 関西 / 中国 / 四国 / 九州 / 沖縄」のいずれかを入力。自社提出主体コードが登録済のエリアのみ入力可能。受渡区分がJEPXの行では沖縄は選択不可。',
    example: '東京',
  },
  {
    name: '精算単価参照エリア',
    description: '精算単価の参照先エリア',
    required: 'conditional',
    rule: '「北海道 / 東北 / 東京 / 中部 / 北陸 / 関西 / 中国 / 四国 / 九州」のいずれかを入力。受渡区分がJEPXの行のみ必須。',
    example: '関西',
  },
  {
    name: '開始日',
    description: '受渡期間の開始日',
    required: 'required',
    rule: '「YYYY/MM/DD」の形式で入力。',
    example: '2026/10/01',
  },
  {
    name: '終了日',
    description: '受渡期間の終了日',
    required: 'required',
    rule: '「YYYY/MM/DD」の形式で入力。',
    example: '2027/01/31',
  },
  {
    name: '日区分',
    description: '数量を適用する曜日区分',
    required: 'conditional',
    rule: '商品形態が定型の場合のみ入力必須 (非定型の場合は空欄必須、値を入れると行エラー)。「全日 / 休日 / 平日」から入力。「平日」「休日」はカンマ区切りで複数選択可能。「全日」は平日・休日と併記できない。時間帯区分と同じ数の項目を、同じ順番で入力する (同じ値を繰り返してもよい。ただし対応する時間帯が重ならないこと)。',
    example: '平日,平日,休日',
  },
  {
    name: '時間帯区分',
    description: '数量を適用する時刻の範囲',
    required: 'conditional',
    rule: '商品形態が定型の場合のみ入力必須 (非定型の場合は空欄必須、値を入れると行エラー)。「HH:mm-HH:mm」の形式で入力。カンマ区切りで複数指定可能。日区分と同じ数の項目を、同じ順番で入力する。',
    example: '0:00-8:00,20:00-24:00,00:00-24:00',
  },
  {
    name: '受給日カレンダー',
    description: '休日判定に使うカレンダー',
    required: 'optional',
    rule: '「北海道 / 東北 / 東京 / 中部 / 北陸 / 関西 / 中国 / 四国 / 九州 / 沖縄 / JEPX / TOCOM / EEX / ICE」のいずれかを入力。日区分が全日以外の場合に、その受渡日が休日かどうかを判定する基準として使う。空欄の場合、受渡区分が JEPX のときは JEPX のカレンダー、BG / TOCOM / EEX / ICE / スワップ のときは自社エリアのカレンダーを使う。',
    example: '',
  },
  {
    name: '数量 (MW)',
    description: '契約数量 (MW)',
    required: 'conditional',
    rule: '0より大きい値で入力。小数点以下3桁まで入力可。小数点3桁目は偶数 (0/2/4/6/8) のみ許容。商品形態が定型の場合のみ必須 (非定型の場合は空欄にする。値を入れると行エラー)。',
    example: '10.002',
  },
  {
    name: '単価 (円/kWh)',
    description: '契約単価',
    required: 'required',
    rule: '0より大きい値で入力。小数点以下5桁まで入力可。',
    example: '12.5',
  },
  {
    name: 'delete',
    description: '論理削除フラグ',
    required: 'optional',
    rule: '入力済みのデータを削除したい場合は「1」で入力。取引IDを照合して既存データがある場合は削除する。',
    example: '1',
  },
]

/**
 * 英語ヘッダー名 (spec 第2節「英語ヘッダー名」列、20 列・列順は
 * `BILATERAL_TRADE_CSV_COLUMNS` と同じ。backend の `BilateralTradeCsvHeaderEnglish`
 * と一致)。記入ガイド表の列名、英語表示の雛形・入稿済みデータの 1 行目、前段チェックの
 * 英語ヘッダー受理 (大文字・小文字は無視) と期待ヘッダーの表示に使う。
 */
export const BILATERAL_TRADE_CSV_COLUMNS_EN = [
  'ExecutionRoute',
  'ExecutionDate',
  'PublicTradeId',
  'OwnBusinessCode',
  'OwnSubmissionEntityCode',
  'PartnerName',
  'PartnerSubmissionEntityCode',
  'ProductType',
  'DeliveryType',
  'BuySell',
  'OwnArea',
  'SettlementPriceArea',
  'StartDate',
  'EndDate',
  'DayCategory',
  'TimeSlots',
  'CalendarKey',
  'Volume',
  'UnitPrice',
  'Delete',
] as const

/** Step1 で受け付けるヘッダー (日英が混在したヘッダーは受理しない。backend の取込と同じ)。 */
const ACCEPTED_HEADERS: AcceptedCsvHeaders = {
  ja: [BILATERAL_TRADE_CSV_COLUMNS],
  en: [BILATERAL_TRADE_CSV_COLUMNS_EN],
}

type BilateralTradeCsvColumnEn = (typeof BILATERAL_TRADE_CSV_COLUMNS_EN)[number]

/** 英語表示の雛形 DL / 入稿済みデータ DL の 1 行目 (英語ヘッダー名)。 */
export const BILATERAL_TRADE_CSV_HEADER_EN =
  BILATERAL_TRADE_CSV_COLUMNS_EN.join(',')

/** 表示言語ごとの雛形 DL / 入稿済みデータ DL の 1 行目。列順は日英で同じ。 */
function bilateralTradeCsvHeaderFor(isEnglish: boolean): string {
  return isEnglish ? BILATERAL_TRADE_CSV_HEADER_EN : BILATERAL_TRADE_CSV_HEADER
}

/**
 * データ種別名 (ダウンロードファイル名の `{データ種別名}`、`common.md` §6 の対応表)。
 * 雛形 (`BilateralTradeCsvUpload`) と入稿済みデータ (`AdminUploads`) の 2 page が使う。
 */
export function bilateralTradeCsvDataTypeName(isEnglish: boolean): string {
  return isEnglish ? 'bilateral-trade' : '相対取引契約'
}

/** 雛形のファイル名 (`{データ種別名}_テンプレート.csv` / 英語表示は `_template.csv`)。 */
export function bilateralTradeCsvTemplateFileName(isEnglish: boolean): string {
  return `${bilateralTradeCsvDataTypeName(isEnglish)}_${isEnglish ? 'template' : 'テンプレート'}.csv`
}

/**
 * 英語表示の記入ガイド表 (spec 第3節「英語設定時は」+ 付録「表示文言（英語）」の逐語)。
 * 列名は第2節の英語ヘッダー名、区分値 (約定経路・商品形態・売買・エリア等) は英語の値で案内する。
 * 行の順・必須区分は日本語の表 ([[BILATERAL_TRADE_CSV_COLUMN_GUIDE]]) と同じ。
 */
const BILATERAL_TRADE_CSV_COLUMN_GUIDE_EN: Array<
  ColumnGuideEntry<BilateralTradeCsvColumnEn>
> = [
  {
    name: 'ExecutionRoute',
    description: 'Market or route through which the trade was made',
    required: 'required',
    rule: 'Enter one of enechain / eSquare Live / Baseload market / Forward market / Constant backup / Other.',
    example: 'enechain',
  },
  {
    name: 'ExecutionDate',
    description: 'Date the trade was made',
    required: 'required',
    rule: 'Enter in YYYY/MM/DD format.',
    example: '2026/09/01',
  },
  {
    name: 'PublicTradeId',
    description: 'Identifier that your company assigns freely',
    required: 'required',
    rule: "Enter a unique value of up to 20 characters. It cannot start with =, +, -, @, or '. For split periods and spreads, enter each part on its own row with a separate trade ID.",
    example: 'TXN-20260901-001',
  },
  {
    name: 'OwnBusinessCode',
    description: 'Provider code of your company that made the contract',
    required: 'required',
    rule: 'Enter the provider code (4-character code) from the Operator tab of OCCTO. Only providers registered as member providers of your own BGs in your area and valid as of the delivery start date can be used (in a joint BG, member providers of other companies can also be used).',
    example: 'R001',
  },
  {
    name: 'OwnSubmissionEntityCode',
    description: 'Your BG code or schedule submitter code',
    required: 'required',
    rule: 'Enter the BG code from the BG tab of OCCTO, or the schedule submitter code from the schedule submitter master.',
    example: 'LB003',
  },
  {
    name: 'PartnerName',
    description: 'Name of the counterparty',
    required: 'conditional',
    rule: 'Enter the counterparty name or abbreviation from the Counterparty tab of OCCTO. Optional (can be blank) when the delivery type is JEPX / TOCOM / EEX / ICE.',
    example: 'Counterparty A',
  },
  {
    name: 'PartnerSubmissionEntityCode',
    description:
      "Counterparty's BG code or schedule submitter code (also identifies the counterparty area)",
    required: 'conditional',
    rule: 'Enter the counterparty code from the Counterparty tab of OCCTO, or the schedule submitter code from the schedule submitter master. The last digit is used as the area code to identify the counterparty area. Optional (can be blank) when the delivery type is JEPX / TOCOM / EEX / ICE.',
    example: 'LDEF3',
  },
  {
    name: 'ProductType',
    description: 'How the volume is expressed',
    required: 'required',
    rule: 'Enter Standard or Non-standard.',
    example: 'Standard',
  },
  {
    name: 'DeliveryType',
    description: 'Delivery route type',
    required: 'required',
    rule: 'Enter one of BG / JEPX / EEX / TOCOM / ICE / Swap. When the trade route is Baseload market or Forward market, only JEPX can be entered. When the trade route is Constant backup, only BG can be entered.',
    example: 'JEPX',
  },
  {
    name: 'BuySell',
    description: "Buy/sell direction from your company's side",
    required: 'required',
    rule: 'Enter Buy or Sell.',
    example: 'Buy',
  },
  {
    name: 'OwnArea',
    description: 'Your area for the trade',
    required: 'required',
    rule: 'Enter one of Hokkaido / Tohoku / Tokyo / Chubu / Hokuriku / Kansai / Chugoku / Shikoku / Kyushu / Okinawa. Only areas where your submission entity code is registered can be entered. Okinawa cannot be selected in rows where the delivery type is JEPX.',
    example: 'Tokyo',
  },
  {
    name: 'SettlementPriceArea',
    description: 'Area referenced for the settlement price',
    required: 'conditional',
    rule: 'Enter one of Hokkaido / Tohoku / Tokyo / Chubu / Hokuriku / Kansai / Chugoku / Shikoku / Kyushu. Required only in rows where the delivery type is JEPX.',
    example: 'Kansai',
  },
  {
    name: 'StartDate',
    description: 'Start date of the delivery period',
    required: 'required',
    rule: 'Enter in YYYY/MM/DD format.',
    example: '2026/10/01',
  },
  {
    name: 'EndDate',
    description: 'End date of the delivery period',
    required: 'required',
    rule: 'Enter in YYYY/MM/DD format.',
    example: '2027/01/31',
  },
  {
    name: 'DayCategory',
    description: 'Day category to which the volume applies',
    required: 'conditional',
    rule: 'Required only when the product type is Standard (must be blank for Non-standard; entering a value causes a row error). Enter from All days / Weekends and holidays / Weekdays. "Weekdays" and "Weekends and holidays" can be combined with commas. "All days" cannot be combined with Weekdays or Weekends and holidays. Enter the same number of items as the time range, in the same order. (The same value can be repeated, as long as the corresponding time ranges do not overlap.)',
    example: 'Weekdays,Weekdays,Weekends and holidays',
  },
  {
    name: 'TimeSlots',
    description: 'Time range to which the volume applies',
    required: 'conditional',
    rule: 'Required only when the product type is Standard (must be blank for Non-standard; entering a value causes a row error). Enter in HH:mm-HH:mm format. Multiple ranges can be separated by commas. Enter the same number of items as the day category, in the same order.',
    example: '0:00-8:00,20:00-24:00,00:00-24:00',
  },
  {
    name: 'CalendarKey',
    description: 'Calendar used to determine holidays',
    required: 'optional',
    rule: "Enter one of Hokkaido / Tohoku / Tokyo / Chubu / Hokuriku / Kansai / Chugoku / Shikoku / Kyushu / Okinawa / JEPX / TOCOM / EEX / ICE. When the day category is not All days, this calendar is used to determine whether each delivery date is a holiday. If left blank, the JEPX calendar is used when the delivery type is JEPX, and your area's calendar is used when it is BG / TOCOM / EEX / ICE / Swap.",
    example: '',
  },
  {
    name: 'Volume',
    description: 'Contract volume (MW)',
    required: 'conditional',
    rule: 'Enter a value greater than 0, with up to 3 decimal places. The third decimal place must be an even digit (0/2/4/6/8). Required only when the product type is Standard (must be blank for Non-standard; entering a value causes a row error).',
    example: '10.002',
  },
  {
    name: 'UnitPrice',
    description: 'Contract unit price',
    required: 'required',
    rule: 'Enter a value greater than 0, with up to 5 decimal places.',
    example: '12.5',
  },
  {
    name: 'Delete',
    description: 'Deletion flag',
    required: 'optional',
    rule: 'To delete registered data, enter 1. If existing data matches the trade ID, it is deleted.',
    example: '1',
  },
]

/** 表示言語に応じた記入ガイド表 (spec 第3節)。 */
export function bilateralTradeCsvColumnGuide(
  isEnglish: boolean,
): Array<
  ColumnGuideEntry<BilateralTradeCsvColumn | BilateralTradeCsvColumnEn>
> {
  return isEnglish
    ? BILATERAL_TRADE_CSV_COLUMN_GUIDE_EN
    : BILATERAL_TRADE_CSV_COLUMN_GUIDE
}

/**
 * 記入例案内の代表列と値。代表列は取引ID で、英語表示では英語ヘッダー名にする (#8673)。
 */
export function bilateralTradeCsvSampleRow(isEnglish: boolean): {
  column: string
  example: string
} {
  return {
    column: isEnglish ? 'PublicTradeId' : '取引ID',
    example: 'SAMPLE-001',
  }
}

/**
 * 雛形の記入例のうち表示言語で変わる値 (区分値とダミー文の取引先名)。英語は backend の
 * 取込が受理する英語の値 (`bilateral_trade_csv_vocab.go`) と spec 英語付録の訳
 * (`Counterparty A` 等)。自由記入・コード値 (取引ID・事業者コード・BG コード・日付・
 * 数量・単価) は訳さないので {@link templateRows} の言語共通の値に置く。
 */
const TEMPLATE_LABELS = {
  ja: {
    counterpartyA: '取引先A',
    counterpartyB: '取引先B',
    standard: '定型',
    nonStandard: '非定型',
    buy: '買',
    sell: '売',
    tokyo: '東京',
    allDays: '全日',
    baseloadMarket: 'ベースロード市場',
  },
  en: {
    counterpartyA: 'Counterparty A',
    counterpartyB: 'Counterparty B',
    standard: 'Standard',
    nonStandard: 'Non-standard',
    buy: 'Buy',
    sell: 'Sell',
    tokyo: 'Tokyo',
    allDays: 'All days',
    baseloadMarket: 'Baseload market',
  },
} as const

/**
 * テンプレート CSV の記入例 4 行を表示言語の値で組み立てる。**値の正本は spec
 * `bilateral-trade.md` 第2節「雛形の記入例データ（4行）」の表**で、代表 4
 * パターン (① 定型 / ② 非定型 / ③ 期間分割の一方の期間〈1件目〉/
 * ④ スプレッドの一方の脚〈1脚目〉) を示す (spec §2 雛形の記入例)。leg 概念の
 * 廃止 (issue #6131) により、1 行 = 1 取引ID の独立契約として表現する。
 * 期間分割・スプレッドの残りの片方は、実際は別の取引IDとして別行で追加
 * 入稿する必要があるが、雛形では代表 1 件のみを示す (spec §2 表下注記)。
 *
 * マスタ依存列 (取引先・自社側事業者コード・各BGコード) はダミー値なので、
 * 記入例行をそのまま入稿するとマスタ検証 (AC-DUB-006 / AC-DUB-041 /
 * AC-DUB-042) で行エラーになり登録されない (= サンプルデータの誤登録事故が
 * 起きない)。Step1 パネル内の記入例案内 (`common:upload.templatePrep.sampleRowHint`
 * に `CsvUploadStep1Panel` の `sampleRow` を差し込んだ文) と対で「自分のデータに
 * 置き換えて提出」を案内する。
 *
 * 取引先BG/計画提出者コードの末尾 1 桁が取引先エリアになる (spec §2「取引先
 * エリアの導出」)。記入例では東京=3 (`LDEF3`)・関西=6 (`LGHJ6`)。
 *
 * ④行は受渡区分=JEPX・約定経路=ベースロード市場のため、取引先・取引先BG/
 * 計画提出者コードを空欄にする。取引所を介した受渡では両列は任意であり、
 * 値を入力した場合だけ通常どおり検証する (spec §2「取引先・取引先BG/計画
 * 提出者コードの必須性」)。
 */
// 記入例の値は backend apitest が逐語で写している
// (go/test/apitest/supply_bilateral_trade_csv_fixtures_test.go
// bilateralCsvTemplateSampleRowBase / bilateralCsvTemplateSampleRows と
// supply_bilateral_trade_csv_english_test.go bilateralCsvEnglishTemplate、
// issue #7816)。ここを変えたら Go 側も同じ値に揃える。
function templateRows(
  label: (typeof TEMPLATE_LABELS)[keyof typeof TEMPLATE_LABELS],
): Array<Record<BilateralTradeCsvColumn, string>> {
  // spec §2 雛形の記入例データ 行1 = ① 定型取引。行2〜4 はこれに上書きを重ねる。
  const base: Record<BilateralTradeCsvColumn, string> = {
    約定経路: 'enechain',
    約定日: '2026/08/20',
    取引ID: 'SAMPLE-001',
    自社側事業者コード: '0001',
    '自社側BG/計画提出者コード': 'LABC3',
    取引先: label.counterpartyA,
    '取引先BG/計画提出者コード': 'LDEF3',
    商品形態: label.standard,
    受渡区分: 'BG',
    売買: label.buy,
    自社エリア: label.tokyo,
    精算単価参照エリア: '',
    開始日: '2026/09/01',
    終了日: '2026/09/30',
    日区分: label.allDays,
    時間帯区分: '0:00-24:00',
    受給日カレンダー: '',
    '数量 (MW)': '50.000',
    '単価 (円/kWh)': '12.50000',
    delete: '',
  }
  return [
    // ① 定型取引
    { ...base },
    // ② 非定型取引 (日区分・時間帯区分・数量 (MW) とも空欄。実数量はコマ別に
    // 48 点データ CSV で反映する。spec §2 雛形の記入例 行2)
    {
      ...base,
      取引ID: 'SAMPLE-002',
      取引先: label.counterpartyB,
      '取引先BG/計画提出者コード': 'LGHJ6',
      商品形態: label.nonStandard,
      売買: label.sell,
      日区分: '',
      時間帯区分: '',
      '数量 (MW)': '',
      '単価 (円/kWh)': '14.80000',
    },
    // ③ 期間分割 (1件目、別取引IDで表現)
    {
      ...base,
      取引ID: 'SAMPLE-003',
      終了日: '2026/09/15',
      '数量 (MW)': '30.000',
      '単価 (円/kWh)': '13.00000',
    },
    // ④ スプレッド (1脚目、別取引IDで表現。取引所を介した匿名取引)。
    {
      ...base,
      約定経路: label.baseloadMarket,
      取引ID: 'SAMPLE-004',
      取引先: '',
      '取引先BG/計画提出者コード': '',
      受渡区分: 'JEPX',
      精算単価参照エリア: label.tokyo,
      '数量 (MW)': '20.000',
      '単価 (円/kWh)': '11.50000',
    },
  ]
}

/** 日本語表示の雛形の記入例 4 行 (spec §2 雛形の記入例データ)。 */
export const BILATERAL_TRADE_CSV_TEMPLATE_ROWS = templateRows(
  TEMPLATE_LABELS.ja,
)

const BILATERAL_TRADE_CSV_TEMPLATE_ROWS_EN = templateRows(TEMPLATE_LABELS.en)

/** 表示言語に応じた雛形の記入例行。 */
export function bilateralTradeCsvTemplateRows(
  isEnglish: boolean,
): Array<Record<BilateralTradeCsvColumn, string>> {
  return isEnglish
    ? BILATERAL_TRADE_CSV_TEMPLATE_ROWS_EN
    : BILATERAL_TRADE_CSV_TEMPLATE_ROWS
}

/**
 * 削除件数の警告バナー閾値 (spec AC-DUB-030: 100 件以上、または既存取引の
 * 50% 超を削除する場合に Step2 で強調警告する)。
 */
export const BILATERAL_TRADE_CSV_DELETE_WARNING_COUNT = 100
export const BILATERAL_TRADE_CSV_DELETE_WARNING_RATIO = 0.5

/**
 * Step2 の削除件数 警告バナーを出すかどうか (spec AC-DUB-030)。
 *
 * 「削除件数が 100 件**以上**、または既存取引の 50% **超**を削除する間」の
 * 2 条件の OR で、**境界の向きが非対称**なのが要点 (100 件ちょうどは発火・
 * 既存のちょうど 50% は非発火)。
 *
 * `deletableTradeCount` は Check レスポンスの `deletable_trade_count` =
 * Check 実行時点で、入稿済みデータの全量ダウンロード対象と同一集合に入る
 * 既存契約の件数 (CSV の delete=1 が実際に受理する集合より狭い。母集団の
 * 定義は backend の `TradingContractRepository.CountDeletableTrades` godoc)。
 * 一覧 RPC の総件数を使わないのは、あちらがフィルタ適用後かつ Check とは
 * 別タイミングの値のため。
 *
 * 0 件のときは比率を評価しない: ゼロ除算で `Infinity` になり、削除行がある
 * 限り常時発火してしまうため (件数条件だけで判定する)。
 *
 * 分母そのものは backend が数えた値をそのまま使い、frontend で再計算しない。
 */
export function shouldWarnBilateralTradeCsvDelete(
  deletedCount: number,
  deletableTradeCount: number,
): boolean {
  if (deletedCount >= BILATERAL_TRADE_CSV_DELETE_WARNING_COUNT) return true
  if (deletableTradeCount <= 0) return false
  return (
    deletedCount / deletableTradeCount >
    BILATERAL_TRADE_CSV_DELETE_WARNING_RATIO
  )
}

/** 解決済みの CSV 1 行 (セル値は生文字列のまま)。 */
export type BilateralTradeCsvRow = {
  /** 1 起算のデータ行番号 (ヘッダー行を除く。backend の row_number と一致)。 */
  rowNumber: number
  cells: Record<BilateralTradeCsvColumn, string>
}

/**
 * parse 結果。`error` が入るのはファイル全体を受け付けられないケース
 * (ヘッダー不一致 / 列数ズレ) だけで、値の業務検証は backend が行う。
 */
export type BilateralTradeCsvParseResult = {
  rows: BilateralTradeCsvRow[]
  error?: string
  /**
   * 囲み (ダブルクォート) が正しくない行の物理行番号 (AC-DUC-045)。入っている時は
   * `error` ではなくこちらで拒否する (文言は表示側が i18n で組む)。
   */
  quoteErrorRow?: number
}

/**
 * 全列が空値のデータ行かどうか (AC-DUC-036: 「当該行を存在しないものとして無視し、
 * 必須項目欠落エラー・差分プレビューのいずれにも計上しない」)。Excel でテンプレートの
 * 不要行をクリアして保存すると、列数ぶんの区切りだけの行 (`,,,,`) が残る。
 * 行番号にも数えない (backend の `ParseBilateralTradeCsv` と同採番)。
 */
function isAllEmptyDataRecord(record: string[]): boolean {
  return (
    record.length === BILATERAL_TRADE_CSV_COLUMNS.length &&
    record.every((cell) => cell.trim() === '')
  )
}

/** ヘッダー不一致 diff で先頭何件まで列挙するか (これを超えると「ほか N 件」に畳む)。 */
const HEADER_DIFF_DISPLAY_LIMIT = 5

/**
 * ヘッダー行の不一致を per-列 diff で整形する (mock-app
 * `SupplyCapacityBilateral.razor.cs` の `ValidateCsvHeader` と同じ pattern)。
 * 列数が違う場合は列名比較をせず件数不一致だけを報告する (列がズレた状態で
 * 名前を突き合わせても無意味な diff になるため)。
 */
function buildHeaderMismatchError(
  header: string[],
  isEnglish: boolean,
): string {
  // 期待ヘッダーは表示言語の列名で示す (common.md §6)。
  const expectedColumns = isEnglish
    ? BILATERAL_TRADE_CSV_COLUMNS_EN
    : BILATERAL_TRADE_CSV_COLUMNS
  if (header.length !== expectedColumns.length) {
    return `ヘッダー列数が一致しません (実際 ${header.length} 列 / 期待 ${expectedColumns.length} 列)。テンプレートの列をそのまま使ってください`
  }
  const diffs: string[] = []
  for (const [i, expected] of expectedColumns.entries()) {
    if (!isHeaderCellOf(header[i], i, isEnglish)) {
      diffs.push(`${i + 1} 列目「${header[i]}」(期待「${expected}」)`)
    }
  }
  const shown = diffs.slice(0, HEADER_DIFF_DISPLAY_LIMIT)
  const rest = diffs.length - shown.length
  return `ヘッダー列名が一致しません: ${shown.join(' / ')}${rest > 0 ? ` ほか ${rest} 件` : ''}`
}

/**
 * `escapeCsvCell` が数式インジェクション対策 (CWE-1236) で前置したアポストロフィ
 * を取り除く (エクスポート出力の逆変換)。 前置されるのは `' = + - @` で始まる
 * 値のみなので除去も同じ条件に限定する。 エクスポートは `'=X` を `''=X` にする
 * ので、 アポストロフィ自体を含むセルも往復で壊れない。 backend の
 * `stripBilateralCsvFormulaGuard` (bilateral_trade_csv_parse.go) と同じ規則
 * (= 前段チェックと本取込で受理範囲がズレると、 画面は通ったのに RPC で落ちる)。
 *
 * **既知の制約**: エクスポートを経ていない手書き CSV の `'=X` (アポストロフィ
 * 自体が値の一部) と、 エクスポートが `=X` に付けたガード `'=X` は区別できない。
 * 前者は `=X` に変わるため、 そういう名称の取引先マスタがある環境では照合エラー
 * になる。 新規に登録する取引IDは AC-DUB-064 で先頭が `' = + - @` の値を登録
 * できなくしたためこの衝突は起きない (規則より前に登録された旧い取引IDは残りうる)。
 * 取引先マスタ等の他の値と、 48 点データ
 * (`models/supply48DataCsv.ts`) / CIS 契約マスタ / 需要グループ の CSV は同じ
 * 規則・同じ制約のままで、 恒久策 (ガード表現を通常値と衝突しない形にする /
 * セル値の禁止文字を定義する) は CSV 横断の契約変更になるため issue #10432 で
 * 扱う。
 */
function stripFormulaGuard(cell: string): string {
  return /^'\s*['=+\-@]/.test(cell) ? cell.slice(1) : cell
}

/**
 * 相対取引契約 CSV のテキストをレコード単位のセル集合に parse する。
 *
 * ヘッダー行は必須で、日本語 20 列 (完全一致) か英語 20 列 (大文字・小文字無視) の
 * どちらかと一致しない場合はファイル全体を拒否する (共通仕様 `common.md` の厳格モード。
 * 日英混在も拒否)。不一致時の期待ヘッダーは `isEnglish` (表示言語) の列名で示す。BOM は除去する。
 *
 * `rowNumber` は物理行ではなくレコード (= クォート内改行を 1 件に畳んだ単位) で
 * 採番するため、backend の `csv.Reader` が返す row_number と一致する。
 */
export function parseBilateralTradeCsv(
  csvText: string,
  isEnglish = false,
): BilateralTradeCsvParseResult {
  const text = csvText.replace(/^\uFEFF/, '')
  // 行を先に分割してから列を分けると、スプレッドシートが自動でクォートするセル
  // (カンマや改行を含む取引先名 / 日区分・時間帯区分の複数指定) で後続列がシフトする。
  // backend は `encoding/csv` で読むため (`bilateral_trade_csv_parse.go`)、
  // 共通のレコード単位の読み取りで囲みも backend と同じ規則で判定する。
  const read = readCsvRecords(text, ',')
  if (!read.ok) return { rows: [], quoteErrorRow: read.quoteErrorRow }
  const records = read.records.map((r) => r.cells)

  if (records.length === 0 || isBlankCsvRecord(records[0])) {
    return { rows: [], error: 'ヘッダー行がありません' }
  }

  const header = records[0].map((c) => c.trim())
  if (!isAcceptedCsvHeader(header, ACCEPTED_HEADERS)) {
    return {
      rows: [],
      error: buildHeaderMismatchError(header, isEnglish),
    }
  }

  const dataRecords = records
    .slice(1)
    .filter((r) => !isBlankCsvRecord(r) && !isAllEmptyDataRecord(r))

  const rows: BilateralTradeCsvRow[] = []
  for (let i = 0; i < dataRecords.length; i++) {
    const cols = dataRecords[i].map((c) => stripFormulaGuard(c.trim()))
    if (cols.length !== BILATERAL_TRADE_CSV_COLUMNS.length) {
      return {
        rows: [],
        error: `行 ${i + 1}: 列数が想定 (${BILATERAL_TRADE_CSV_COLUMNS.length} 列) と一致しません (実際 ${cols.length} 列)。セル内の "..." エスケープや末尾のカンマ、余分な空カラムを確認してください`,
      }
    }
    const cells = {} as Record<BilateralTradeCsvColumn, string>
    for (const [index, col] of BILATERAL_TRADE_CSV_COLUMNS.entries()) {
      cells[col] = cols[index]
    }
    rows.push({ rowNumber: i + 1, cells })
  }
  return { rows }
}

/**
 * 行を CSV テキストへ戻す (テンプレート DL / 入稿済みデータ DL の往復互換)。
 * `parseBilateralTradeCsv(serializeBilateralTradeCsvRows(rows)).rows` は元の
 * セル値と一致する。英語表示では 1 行目を英語ヘッダー名にする (行の値は日本語の列名を
 * key のまま出す。列順は日英で同じ)。
 */
export function serializeBilateralTradeCsvRows(
  rows: Array<Partial<Record<BilateralTradeCsvColumn, string>>>,
  isEnglish = false,
): string {
  const lines = [bilateralTradeCsvHeaderFor(isEnglish)]
  for (const row of rows) {
    lines.push(
      BILATERAL_TRADE_CSV_COLUMNS.map((col) =>
        escapeCsvCell(row[col] ?? ''),
      ).join(','),
    )
  }
  return `${lines.join('\n')}\n`
}

const KOMA_MINUTES = 30
const MINUTES_PER_DAY = 24 * 60

/**
 * 1 起算のコマ番号集合を `"HH:mm-HH:mm,HH:mm-HH:mm"` へ復元する (= 入稿済み
 * データ DL / 編集フォーム再表示の往復可逆性)。区切り文字はカンマ (spec §2
 * 列17。2026-08-17 に旧 "|" から "," へ変更)。連続するコマは 1 区間に畳み、
 * 末尾のコマ 48 の終端は "24:00" で表す。
 */
export function komaNumbersToTimeSlotText(komas: number[]): string {
  if (komas.length === 0) return ''
  const sorted = [...new Set(komas)].sort((a, b) => a - b)
  const slots: string[] = []
  let runStart = sorted[0]
  let prev = sorted[0]
  const push = (from: number, to: number): void => {
    slots.push(`${komaNumberToTime(from)}-${komaEndTime(komaNumberToTime(to))}`)
  }
  for (const v of sorted.slice(1)) {
    if (v === prev + 1) {
      prev = v
      continue
    }
    push(runStart, prev)
    runStart = v
    prev = v
  }
  push(runStart, prev)
  return slots.join(',')
}

/**
 * `"HH:mm-HH:mm,..."` を 1 起算のコマ番号集合へ変換する。30 分境界に乗らない /
 * 逆順 / 範囲外の区間は `undefined` を返す (= 黙って丸めない)。
 *
 * backend が同じ変換を行うため、ここは編集フォーム再表示とテンプレ往復のための
 * 補助であって、取込の可否を決めるものではない。
 */
export function timeSlotTextToKomaNumbers(text: string): number[] | undefined {
  const komas = new Set<number>()
  for (const part of text.split(',')) {
    const slot = part.trim()
    if (slot === '') continue
    const sep = slot.indexOf('-')
    if (sep < 0) return undefined
    const start = parseSlotMinutes(slot.slice(0, sep).trim(), false)
    const end = parseSlotMinutes(slot.slice(sep + 1).trim(), true)
    if (start === undefined || end === undefined || end <= start) {
      return undefined
    }
    for (let m = start; m < end; m += KOMA_MINUTES) {
      komas.add(m / KOMA_MINUTES + 1)
    }
  }
  if (komas.size === 0) return undefined
  return [...komas].sort((a, b) => a - b)
}

function parseSlotMinutes(
  raw: string,
  allowEndOfDay: boolean,
): number | undefined {
  const m = /^(\d{1,2}):(\d{2})$/.exec(raw)
  if (m === null) return undefined
  const hours = Number(m[1])
  const minutes = Number(m[2])
  if (minutes % KOMA_MINUTES !== 0) return undefined
  const total = hours * 60 + minutes
  const limit = allowEndOfDay ? MINUTES_PER_DAY : MINUTES_PER_DAY - KOMA_MINUTES
  if (total < 0 || total > limit) return undefined
  return total
}
